import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { RefreshCw, Search, Trash2, Terminal, FileText } from 'lucide-react';
import { logsApi } from '../../api/endpoints/logs';
import { useDebounce } from '../../hooks/useDebounce';
import { useSocket } from '../../hooks/useSocket';
import { queryClient } from '../../providers/QueryProvider';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatDate } from '../../utils/formatters';

function maskId(str?: string) {
  if (!str) return '';
  if (str.length <= 5) return str;
  return '*'.repeat(str.length - 5) + str.slice(-5);
}

function formatJson(val: unknown): string {
  if (!val) return '';
  if (typeof val === 'object') return JSON.stringify(val, null, 2);
  try { return JSON.stringify(JSON.parse(String(val)), null, 2); } catch { return String(val); }
}

function ResponseBadge({ status }: { status?: number }) {
  if (!status) return <span className="text-gray-400 dark:text-gray-500 text-xs">--</span>;
  const variant = status < 300 ? 'green' : status < 400 ? 'yellow' : 'red';
  return <Badge variant={variant}>{status}</Badge>;
}

function ExpandableJson({ label, data, color }: { label: string; data: unknown; color: 'blue' | 'purple' }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const btnColor = color === 'blue'
    ? 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
    : 'text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300';
  const bgColor = color === 'blue'
    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
  return (
    <>
      <button onClick={() => setOpen(!open)} className={`ml-1 text-xs font-mono ${btnColor}`}>
        [{label}]
      </button>
      {open && (
        <pre className={`mt-1 p-2 rounded text-xs text-gray-700 dark:text-gray-300 font-mono whitespace-pre-wrap max-h-40 overflow-auto border ${bgColor}`}>
          {formatJson(data)}
        </pre>
      )}
    </>
  );
}

const eventBadgeVariant: Record<string, 'green' | 'red' | 'blue' | 'yellow' | 'gray'> = {
  connect: 'green',
  disconnect: 'red',
  auth_success: 'blue',
  auth_fail: 'yellow',
  error: 'red',
  tunnel_open: 'gray',
  tunnel_close: 'gray',
};

const eventLabels: Record<string, string> = {
  connect: 'Connect',
  disconnect: 'Disconnect',
  auth_success: 'Auth OK',
  auth_fail: 'Auth Fail',
  error: 'Error',
  tunnel_open: 'Tunnel Open',
  tunnel_close: 'Tunnel Close',
};

interface StatCardDef {
  key: string;
  label: string;
  color: string;
  bgColor: string;
  ringColor: string;
  getValue: (events: Record<string, number>) => number;
  filterValue: string;
}

const statCards: StatCardDef[] = [
  {
    key: 'connect',
    label: 'Connects',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    ringColor: 'ring-green-500',
    getValue: (e) => e.connect || 0,
    filterValue: 'connect',
  },
  {
    key: 'disconnect',
    label: 'Disconnects',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    ringColor: 'ring-red-500',
    getValue: (e) => e.disconnect || 0,
    filterValue: 'disconnect',
  },
  {
    key: 'auth_success',
    label: 'Auth OK',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    ringColor: 'ring-blue-500',
    getValue: (e) => e.auth_success || 0,
    filterValue: 'auth_success',
  },
  {
    key: 'auth_fail',
    label: 'Auth Fail',
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    ringColor: 'ring-yellow-500',
    getValue: (e) => e.auth_fail || 0,
    filterValue: 'auth_fail',
  },
  {
    key: 'other',
    label: 'Other',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    ringColor: 'ring-gray-500',
    getValue: (e) => (e.error || 0) + (e.tunnel_open || 0) + (e.tunnel_close || 0),
    filterValue: 'error',
  },
];

// =====================================================================
// Application Logs Panel (live journalctl + SSE streaming)
// =====================================================================
function AppLogsPanel() {
  const [lines, setLines] = useState(100);
  const [filter, setFilter] = useState('');
  const [live, setLive] = useState(false);
  const [liveLines, setLiveLines] = useState<string[]>([]);
  const logEndRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const debouncedFilter = useDebounce(filter);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['app-logs', lines, debouncedFilter],
    queryFn: () => logsApi.appLogs(lines, debouncedFilter),
    refetchInterval: live ? false : 10000,
  });

  const appLogs = data?.logs || [];

  // SSE live streaming
  useEffect(() => {
    if (!live) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }

    setLiveLines([]);
    const es = new EventSource('/api/app-logs/stream', { withCredentials: true });
    eventSourceRef.current = es;

    es.onopen = () => {
      setLiveLines(prev => [...prev, '[Connected to log stream]']);
    };

    es.onmessage = (event) => {
      try {
        const line = JSON.parse(event.data);
        setLiveLines(prev => {
          const next = [...prev, line];
          return next.length > 500 ? next.slice(-500) : next;
        });
      } catch {
        // Raw string fallback
        if (event.data) {
          setLiveLines(prev => {
            const next = [...prev, event.data];
            return next.length > 500 ? next.slice(-500) : next;
          });
        }
      }
    };

    es.onerror = () => {
      setLiveLines(prev => [...prev, '[Stream error - reconnecting...]']);
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [live]);

  // Auto-scroll when live
  useEffect(() => {
    if (live && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [liveLines, live]);

  const displayLines = live ? liveLines : appLogs;

  const colorize = (line: string) => {
    if (/error|Error|ERROR|fail|FAIL|crash/i.test(line)) return 'text-red-400';
    if (/warn|WARN|Warning/i.test(line)) return 'text-yellow-400';
    if (/\[AlertEval\]|\[AlertEmail\]/i.test(line)) return 'text-orange-400';
    if (/connected|started|success|listening/i.test(line)) return 'text-green-400';
    return 'text-gray-300';
  };

  return (
    <div>
      {/* Controls */}
      <div className="bg-white dark:bg-[#111] rounded-md shadow-sm border border-gray-200 dark:border-gray-800 p-3 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={16} className="absolute left-2.5 top-2 text-gray-400" />
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filter logs..."
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
              disabled={live}
            />
          </div>
          <select
            value={lines}
            onChange={e => setLines(Number(e.target.value))}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
            disabled={live}
          >
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
            <option value={200}>Last 200</option>
            <option value={500}>Last 500</option>
          </select>
          <button
            onClick={() => setLive(l => !l)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition-colors ${
              live
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <span className={`inline-block w-2 h-2 rounded-full ${live ? 'bg-red-300 animate-pulse' : 'bg-green-300'}`} />
            {live ? 'Stop Live' : 'Go Live'}
          </button>
          {!live && (
            <button
              onClick={() => refetch()}
              className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 text-xs"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Log output */}
      <div className="bg-gray-900 rounded-lg border border-gray-700 shadow-sm">
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Terminal size={14} className="text-gray-400" />
            <span className="text-xs text-gray-400 font-mono">restoroworks-connect.service</span>
            {live && <span className="text-xs text-green-400 animate-pulse">LIVE</span>}
          </div>
          <span className="text-xs text-gray-500">
            {displayLines.length} lines
            {' | '}
            {isLoading ? 'loading' : error ? 'error' : `fetched ${appLogs.length}`}
            {live ? ` | live: ${liveLines.length}` : ''}
          </span>
        </div>
        <div className="overflow-auto font-mono text-xs leading-5 p-4" style={{ maxHeight: 'calc(100vh - 320px)', minHeight: 400 }}>
          {error ? (
            <div className="text-red-400 text-center py-8">Error loading logs: {(error as Error).message}</div>
          ) : isLoading && !live ? (
            <div className="text-gray-500 text-center py-8">Loading logs...</div>
          ) : displayLines.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              {live ? 'Waiting for new log entries...' : 'No log entries found'}
            </div>
          ) : (
            displayLines.map((line, i) => (
              <div key={i} className={`${colorize(line)} hover:bg-gray-800/50 px-1 -mx-1 rounded`}>
                {line}
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Main LogsPage with tabs
// =====================================================================
export function LogsPage() {
  const [activeTab, setActiveTab] = useState<'access' | 'application'>('access');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [eventType, setEventType] = useState('');
  const [purging, setPurging] = useState(false);

  const debouncedSearch = useDebounce(search);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['logs', { page, limit, event_type: eventType, search: debouncedSearch }],
    queryFn: () =>
      logsApi.list({ page, per_page: limit, event_type: eventType || undefined, search: debouncedSearch || undefined }),
    enabled: activeTab === 'access',
  });

  const { data: statsData } = useQuery({
    queryKey: ['logs-stats'],
    queryFn: () => logsApi.stats(),
    refetchInterval: 60_000,
    enabled: activeTab === 'access',
  });

  // Real-time: new log entries
  useSocket(
    'newLog',
    useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      queryClient.invalidateQueries({ queryKey: ['logs-stats'] });
    }, []),
  );

  const logs = data?.logs || [];
  const totalPages = data?.totalPages || 1;
  const totalCount = data?.total || 0;
  const perPage = data?.perPage || limit;
  const stats = statsData?.stats;
  const events = stats?.events || { connect: 0, disconnect: 0, auth_success: 0, auth_fail: 0, error: 0 };

  const handleStatClick = (filterValue: string) => {
    setEventType((prev) => (prev === filterValue ? '' : filterValue));
    setPage(1);
  };

  const handlePurge = async () => {
    if (!window.confirm('Delete logs older than 90 days?')) return;
    setPurging(true);
    try {
      const result = await logsApi.purge();
      window.alert(`Deleted ${result.deleted} old log entries`);
      queryClient.invalidateQueries({ queryKey: ['logs'] });
    } catch {
      window.alert('Failed to purge logs');
    } finally {
      setPurging(false);
    }
  };

  if (isLoading && !data && !statsData && activeTab === 'access') return <PageSpinner />;

  return (
    <div>
      {/* Header with Tabs */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Logs</h1>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">
            {activeTab === 'access'
              ? (totalCount > 0 ? `${totalCount.toLocaleString()} total entries` : 'Access log entries')
              : 'Live application server logs'}
          </p>
        </div>
        {activeTab === 'access' && (
          <div className="flex items-center gap-2">
            <button
              onClick={handlePurge}
              disabled={purging}
              className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 px-2 py-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-1 disabled:opacity-50"
              title="Clear logs older than 90 days"
            >
              <Trash2 size={14} />
              Purge
            </button>
            <button
              onClick={() => refetch()}
              className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 text-xs"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('access')}
          className={`px-4 py-2 text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors ${
            activeTab === 'access'
              ? 'border-purple-600 text-purple-600 dark:text-purple-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          <FileText size={14} />
          Access Logs
        </button>
        <button
          onClick={() => setActiveTab('application')}
          className={`px-4 py-2 text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors ${
            activeTab === 'application'
              ? 'border-purple-600 text-purple-600 dark:text-purple-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          <Terminal size={14} />
          Application Logs
        </button>
      </div>

      {activeTab === 'application' ? (
        <AppLogsPanel />
      ) : (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {statCards.map((card) => (
              <div
                key={card.key}
                onClick={() => handleStatClick(card.filterValue)}
                className={`bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-2 flex items-center gap-2 cursor-pointer hover:shadow-md transition-all ${
                  eventType === card.filterValue ? `ring-2 ${card.ringColor}` : ''
                }`}
              >
                <div className={`w-6 h-6 rounded-full ${card.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <span className={`text-xs font-bold ${card.color}`}>
                    {card.key === 'connect' && '\u2197'}
                    {card.key === 'disconnect' && '\u2199'}
                    {card.key === 'auth_success' && '\u2713'}
                    {card.key === 'auth_fail' && '\u2717'}
                    {card.key === 'other' && '\u2022'}
                  </span>
                </div>
                <div>
                  <p className={`text-sm font-bold ${card.color}`}>{card.getValue(events)}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">{card.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-[#111] rounded-md shadow-sm border border-gray-200 dark:border-gray-800 p-3 mb-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[200px] relative">
                <Search size={16} className="absolute left-2.5 top-2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search logs..."
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
                />
              </div>
              <select
                value={eventType}
                onChange={(e) => {
                  setEventType(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Events</option>
                <option value="connect">Connect</option>
                <option value="disconnect">Disconnect</option>
                <option value="auth_success">Auth Success</option>
                <option value="auth_fail">Auth Failed</option>
                <option value="error">Error</option>
                <option value="tunnel_open">Tunnel Open</option>
                <option value="tunnel_close">Tunnel Close</option>
              </select>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
              >
                <option value={25}>25/page</option>
                <option value={50}>50/page</option>
                <option value={100}>100/page</option>
                <option value={200}>200/page</option>
              </select>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 overflow-x-auto">
            <table className="w-full" style={{ minWidth: 900 }}>
              <Thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Event</Th>
                  <Th>Client</Th>
                  <Th>Partner</Th>
                  <Th>Deployment</Th>
                  <Th>Source</Th>
                  <Th>Details</Th>
                  <Th>Response</Th>
                </tr>
              </Thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {logs.map((log) => {
                  const variant = eventBadgeVariant[log.event_type] || 'gray';
                  const label = eventLabels[log.event_type] || log.event_type;
                  const isError = log.event_type === 'error' || log.event_type === 'auth_fail';
                  return (
                    <tr key={log._id} className={`hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${isError ? 'border-l-2 border-l-red-500' : ''}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs" style={{ width: 140 }}>
                        <span className="font-mono text-gray-500 dark:text-gray-400">{formatDate(log.timestamp)}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs" style={{ width: 90 }}>
                        <Badge variant={variant}>{label}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs" style={{ width: 130, maxWidth: 130 }}>
                        <span className="font-medium text-gray-800 dark:text-gray-200 block truncate" title={log.deployment_name || log.client_name || log.client_id || '--'}>
                          {log.deployment_name || log.client_name || log.client_id || '--'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs" style={{ width: 100, maxWidth: 100 }}>
                        {log.partner_name ? (
                          <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded text-xs font-medium inline-block max-w-full truncate" title={log.partner_name}>
                            {log.partner_name}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 text-xs">--</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs" style={{ width: 170, maxWidth: 170 }}>
                        {log.deployment_id ? (
                          <Link to={`/clients/${log.client_id}`} className="block truncate hover:underline" title={log.deployment_name || ''}>
                            <span className="text-xs font-medium text-purple-600 dark:text-purple-400">{log.deployment_name || ''}</span>
                            <br />
                            <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{maskId(log.deployment_id)}</span>
                          </Link>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 text-xs">--</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs" style={{ width: 110 }}>
                        <span className="text-gray-500 dark:text-gray-400 font-mono">
                          {log.source_ip || '--'}{log.source_port ? `:${log.source_port}` : ''}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <span>{log.details || ''}</span>
                        <ExpandableJson label="req" data={log.request_body} color="blue" />
                        <ExpandableJson label="res" data={log.raw_log} color="purple" />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs" style={{ width: 60 }}>
                        <ResponseBadge status={log.response_status} />
                      </td>
                    </tr>
                  );
                })}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                      No logs found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 0 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing {totalCount > 0 ? (page - 1) * perPage + 1 : 0}
                &ndash;{Math.min(page * perPage, totalCount)} of {totalCount.toLocaleString()}
              </p>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} className="mt-0" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
